import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Progress } from 'antd';
import {
  DownloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import { ExecuteResult } from '../registry';
import { makeT } from '../messages';

type Props = {
  executing: boolean;
  progress: { done: number; total: number };
  result: ExecuteResult | null;
};

const ExecuteStep: React.FC<Props> = ({ executing, progress, result }) => {
  const intl = useIntl();
  const t = makeT(intl);

  const failedCsv = useMemo(() => {
    if (!result) return [];
    return [
      ['id', 'referralCode', 'email', 'error'],
      ...result.failed.map((f) => [
        f.record?.id ?? '',
        f.record?.referralCode ?? '',
        f.record?.email ?? '',
        f.error,
      ]),
    ];
  }, [result]);

  if (executing || !result) {
    const percent =
      progress.total > 0
        ? Math.round((progress.done / progress.total) * 100)
        : 0;
    return (
      <div style={{ padding: '60px 0' }}>
        <div className="ba-label" style={{ textAlign: 'center' }}>
          {t('fleetadmin.bulk.execute.progress', {
            done: progress.done,
            total: progress.total,
          })}
        </div>
        <Progress percent={percent} status="active" />
      </div>
    );
  }

  const submitted = result.succeeded.length + result.failed.length;
  const hasErrors = result.failed.length > 0;

  return (
    <div>
      <div className="ba-result-head">
        <div className={`ba-result-icon${hasErrors ? '' : ' ok'}`}>
          {hasErrors ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
        </div>
        <div>
          <div className="ba-result-title">
            {hasErrors
              ? t('fleetadmin.bulk.execute.doneErrors')
              : t('fleetadmin.bulk.execute.doneOk')}
          </div>
          <div className="ba-result-sub">
            {t('fleetadmin.bulk.execute.archivedCount', {
              count: result.succeeded.length,
            })}
            {hasErrors && (
              <>
                ,{' '}
                <span style={{ color: '#EE4E4E' }}>
                  {t('fleetadmin.bulk.execute.failedCount', {
                    count: result.failed.length,
                  })}
                </span>
              </>
            )}
            .
          </div>
        </div>
      </div>

      <div className="ba-stat-grid three">
        <div className="ba-stat">
          <div className="ba-stat-label">
            {t('fleetadmin.bulk.execute.submitted')}
          </div>
          <div className="ba-stat-value">{submitted}</div>
        </div>
        <div className="ba-stat">
          <div className="ba-stat-label">
            {t('fleetadmin.bulk.execute.succeeded')}
          </div>
          <div className="ba-stat-value green">{result.succeeded.length}</div>
        </div>
        <div className="ba-stat">
          <div className="ba-stat-label">
            {t('fleetadmin.bulk.execute.failed')}
          </div>
          <div className="ba-stat-value red">{result.failed.length}</div>
        </div>
      </div>

      {hasErrors && (
        <div className="ba-error-box">
          <div className="ba-error-title">
            {t('fleetadmin.bulk.execute.couldNotArchive', {
              count: result.failed.length,
            })}
          </div>
          <CSVLink
            data={failedCsv}
            filename={`failed-rows-${moment().format('YYYY_MM_DD')}.csv`}
            className="ba-csv-link"
          >
            <DownloadOutlined /> {t('fleetadmin.bulk.execute.downloadFailed')}
          </CSVLink>
        </div>
      )}
    </div>
  );
};

export default ExecuteStep;
